import { format, differenceInDays } from "date-fns";
import { formatCurrency } from "./currency";
import { type Subscription } from "@/types/subscription";

/**
 * Calculate lifecycle statistics for a subscription
 */
export function getLifecycleStats(subscription: Subscription) {
  const { lifecycleEvents, subscriptionPeriods } = subscription;

  // Count reactivations (cancelled -> active transitions)
  const reactivationCount = lifecycleEvents.filter(
    (event) => event.event === "reactivated",
  ).length;

  // Count total cancellations
  const cancellationCount = lifecycleEvents.filter(
    (event) => event.event === "cancelled",
  ).length;

  // Calculate average active period length
  const completedPeriods = subscriptionPeriods.filter(
    (period) => period.endDate,
  );
  const averageActiveDays =
    completedPeriods.length > 0
      ? completedPeriods.reduce(
          (sum, period) =>
            sum + differenceInDays(period.endDate!, period.startDate),
          0,
        ) / completedPeriods.length
      : null;

  // Current active period (if any)
  const currentPeriod = subscriptionPeriods.find((period) => !period.endDate);
  const currentActiveDays = currentPeriod
    ? differenceInDays(new Date(), currentPeriod.startDate)
    : null;

  // Calculate total lifetime value
  const totalLifetimeValue = subscriptionPeriods.reduce(
    (sum, period) => sum + period.totalAmount,
    0,
  );

  // Determine user pattern
  const isFrequentCanceller = cancellationCount >= 2;
  const isSeasonalUser = averageActiveDays && averageActiveDays < 180; // Less than 6 months
  const isLongTermUser = averageActiveDays && averageActiveDays > 365; // More than 1 year

  return {
    reactivationCount,
    cancellationCount,
    averageActiveDays,
    currentActiveDays,
    totalLifetimeValue,
    isFrequentCanceller,
    isSeasonalUser,
    isLongTermUser,
    completedPeriodsCount: completedPeriods.length,
    currentPeriod,
  };
}

/**
 * Get display text for lifecycle badge
 */
export function getLifecycleBadgeText(
  subscription: Subscription,
): string | null {
  const stats = getLifecycleStats(subscription);

  if (stats.reactivationCount >= 3) {
    return `${stats.reactivationCount + 1}x subscriber`;
  }

  if (stats.reactivationCount >= 1) {
    return `${stats.reactivationCount + 1}nd time user`;
  }

  if (stats.isSeasonalUser && stats.completedPeriodsCount >= 2) {
    return "Seasonal user";
  }

  if (stats.isLongTermUser) {
    return "Long-term user";
  }

  return null;
}

/**
 * Get contextual information for cancelled subscriptions
 */
export function getCancelledSubscriptionContext(subscription: Subscription) {
  const stats = getLifecycleStats(subscription);
  const lastPeriod = subscription.subscriptionPeriods
    .filter((p) => p.endDate)
    .sort(
      (a, b) => new Date(b.endDate!).getTime() - new Date(a.endDate!).getTime(),
    )[0];

  if (!lastPeriod) {
    return null;
  }

  const activeDuration = differenceInDays(
    lastPeriod.endDate!,
    lastPeriod.startDate,
  );
  const monthsActive = Math.round(activeDuration / 30);

  // Calculate typical reactivation pattern
  const reactivations = subscription.lifecycleEvents
    .filter((event) => event.event === "reactivated")
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  let typicalBreakDuration: number | null = null;
  if (reactivations.length >= 2) {
    const breakDurations = reactivations
      .slice(1)
      .map((reactivation) => {
        const previousCancellation = subscription.lifecycleEvents
          .filter(
            (event) =>
              event.event === "cancelled" &&
              new Date(event.date) < new Date(reactivation.date),
          )
          .sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
          )[0];

        if (previousCancellation) {
          return differenceInDays(
            new Date(reactivation.date),
            new Date(previousCancellation.date),
          );
        }
        return null;
      })
      .filter(Boolean) as number[];

    if (breakDurations.length > 0) {
      typicalBreakDuration = Math.round(
        breakDurations.reduce((sum, duration) => sum + duration, 0) /
          breakDurations.length,
      );
    }
  }

  return {
    lastActiveStartDate: lastPeriod.startDate,
    lastActiveEndDate: lastPeriod.endDate!,
    activeDuration: activeDuration,
    monthsActive,
    totalAmount: lastPeriod.totalAmount,
    paymentCount: lastPeriod.totalPayments,
    typicalBreakDuration,
    averageActiveDays: stats.averageActiveDays,
  };
}

/**
 * Get reactivation suggestions for cancelled subscriptions
 */
export function getReactivationSuggestions(subscription: Subscription) {
  const context = getCancelledSubscriptionContext(subscription);
  const stats = getLifecycleStats(subscription);

  if (!context) {
    return null;
  }

  const daysSinceCancellation = differenceInDays(
    new Date(),
    context.lastActiveEndDate,
  );

  // Seasonal pattern detection
  const currentMonth = new Date().getMonth();
  const lastActiveMonths = subscription.subscriptionPeriods
    .filter((p) => p.endDate)
    .map((p) => ({
      startMonth: new Date(p.startDate).getMonth(),
      endMonth: new Date(p.endDate!).getMonth(),
    }));

  const isInTypicalActiveMonth = lastActiveMonths.some(
    (period) =>
      currentMonth >= period.startMonth && currentMonth <= period.endMonth,
  );

  let suggestion = "";
  let urgency: "low" | "medium" | "high" = "low";

  if (
    context.typicalBreakDuration &&
    daysSinceCancellation >= context.typicalBreakDuration * 0.8
  ) {
    suggestion = `You typically reactivate after ${Math.round(context.typicalBreakDuration / 30)} months`;
    urgency = "high";
  } else if (isInTypicalActiveMonth && stats.isSeasonalUser) {
    suggestion = "You're usually active during this time of year";
    urgency = "medium";
  } else if (stats.reactivationCount >= 2) {
    suggestion = `Based on your pattern, you might reactivate in ${Math.round((context.typicalBreakDuration || 90) / 30)} months`;
    urgency = "low";
  }

  return suggestion ? { suggestion, urgency } : null;
}

/**
 * Format lifecycle context for display
 */
export function formatLifecycleContext(subscription: Subscription): {
  primary: string;
  secondary?: string;
  badge?: string;
} {
  const context = getCancelledSubscriptionContext(subscription);
  const badge = getLifecycleBadgeText(subscription);

  if (!context) {
    return { primary: "No previous activity", badge: badge || undefined };
  }

  const primary = `Last active: ${context.monthsActive} month${context.monthsActive !== 1 ? "s" : ""} (${format(context.lastActiveStartDate, "MMM")}–${format(context.lastActiveEndDate, "MMM yyyy")}) • ${formatCurrency(context.totalAmount, "USD")} total`;

  let secondary = "";
  if (context.typicalBreakDuration) {
    const avgBreakMonths = Math.round(context.typicalBreakDuration / 30);
    secondary = `Typical break: ${avgBreakMonths} month${avgBreakMonths !== 1 ? "s" : ""}`;
  }

  return {
    primary,
    secondary: secondary || undefined,
    badge: badge || undefined,
  };
}
